// =============================================================================
// SIREPPAR - Renombrar documento / moverlo de sección
// =============================================================================
// Un solo componente para las dos acciones, porque en la práctica se hacen
// juntas: un archivo mal clasificado casi siempre está además mal nombrado.
//
// Quién puede qué (lo vuelve a validar el backend en PATCH /documentos/:id):
//   - Interno (ADMIN / SUPERVISOR / ANALISTA / AUTORIDAD_FIRMANTE): nombre y tipo.
//   - Partido (roles externos): SOLO el nombre de sus propios documentos.
//
// Se descartó el drag & drop entre secciones: las secciones son acordeones
// colapsables con estructuras internas distintas (obligatorios, agrupada por
// tipo, DNIs por persona, símbolos), así que arrastrar exigiría que todas
// aceptaran drop y que el usuario tuviera abierta la de destino. El selector
// llega a cualquier tipo con la sección cerrada y en dos clics.
//
// Cambiar el tipo NO toca el estado de verificación: el archivo es el mismo y
// quien lo mueve del lado interno es el propio revisor.
// =============================================================================

import { createContext, useContext, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Modal from './Modal';
import TipoDocumentoSelect from './TipoDocumentoSelect';
import { documentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { tipoDocLabels, TIPOS_RESOLUCIONES, TIPOS_DNI_PERSONA } from '../../utils/tiposDocumento';
import { MAX_MOTIVO_DOCUMENTO } from '../../utils/limites';

// Roles internos que pueden editar metadatos. MESA_ENTRADAS queda afuera a
// propósito: en gestión es de solo lectura. Espeja ROLES_EDITAR_DOC del backend.
const ROLES_EDITAR = ['ADMINISTRADOR', 'SUPERVISOR', 'ANALISTA', 'AUTORIDAD_FIRMANTE'];

// Documentos que genera el sistema: su tipo es lo que les da sentido, así que no
// se renombran ni se mueven. Espeja TIPOS_NO_EDITABLES del backend.
const TIPOS_NO_EDITABLES = [
  'NOTA',
  'CONSTANCIA_REEMPADRONAMIENTO',
  ...TIPOS_RESOLUCIONES,
];

// Atados a una persona por `dniPersona`: se renombran, pero no cambian de tipo
// (el frente y el dorso los administra la sección de DNIs).
const TIPOS_DNI = [...TIPOS_DNI_PERSONA, 'DNI_FRENTE_INICIADOR', 'DNI_DORSO_INICIADOR', 'DNI_AUTORIDAD'];

export interface DocumentoEditable {
  id: string;
  tipo: string;
  titulo?: string;
  partidoId?: string;
  virtual?: boolean;
  /** Estado de la dimensión que se está viendo (proyectado por la página). */
  estadoRevision?: 'PENDIENTE' | 'APROBADO' | 'RECHAZADO';
  /** Motivo ya escrito en esa dimensión. */
  observacion?: string;
}

/**
 * Agrupaciones cuyos documentos este usuario externo puede renombrar, además de su
 * propio partido.
 *
 * Es para las ALIANZAS: el expediente cuelga de la alianza, así que sus documentos
 * llevan el `partidoId` de la alianza y no el del partido del apoderado integrante.
 * Con la comparación directa el botón desaparecía sobre un expediente que el
 * integrante sí puede gestionar (responde observaciones y sube archivos).
 *
 * Va por contexto y no por prop porque el botón se cuelga en las filas de cuatro
 * componentes de sección distintos: pasarlo a mano obligaba a atravesarlos todos
 * para un dato que es de la pantalla, no de la fila.
 *
 * Es sólo para no mostrar un botón que iba a dar 403. La autorización real la hace
 * PATCH /documentos/:id.
 */
export const AgrupacionesConAccesoContext = createContext<Array<string | null | undefined>>([]);

interface Props {
  doc: DocumentoEditable;
  /** Tipos a los que se puede mover. Por defecto, todos los del selector. */
  tiposPermitidos?: string[];
  /**
   * Dimensión sobre la que se corrige el motivo (la que muestra la página).
   * Sin esto el campo de motivo no aparece: escribir en la dimensión equivocada
   * sería peor que no ofrecerlo.
   */
  etapa?: 'ADMISIBILIDAD' | 'ANALISIS';
  /** Se ejecuta después de guardar, además de invalidar las queries de documentos. */
  onSaved?: () => void;
}

/**
 * Botón "Editar" + su modal. Se renderiza `null` si el usuario no puede editar
 * este documento, así que se puede colgar en cualquier fila sin condicionales.
 */
export default function EditarDocumentoModal({ doc, tiposPermitidos, etapa, onSaved }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [tipo, setTipo] = useState('');
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);

  const esInterno = ROLES_EDITAR.includes(user?.rol || '');
  // El listado que ve un externo ya viene filtrado a su propio partido por el
  // backend, así que si el documento no trae `partidoId` alcanza con que el
  // usuario tenga uno. La validación real la hace igual PATCH /documentos/:id.
  const agrupacionesConAcceso = useContext(AgrupacionesConAccesoContext);
  const propias = new Set(
    [user?.partidoId, ...agrupacionesConAcceso].filter((x): x is string => !!x),
  );
  const esDelPartido =
    !!user?.partidoId && (doc.partidoId === undefined || propias.has(doc.partidoId));
  const puedeEditar =
    !doc.virtual && !TIPOS_NO_EDITABLES.includes(doc.tipo) && (esInterno || esDelPartido);
  // Mover de sección es exclusivo del lado interno, y nunca para los DNI.
  const puedeMover = esInterno && !TIPOS_DNI.includes(doc.tipo);
  // El motivo de la revisión lo escribe la Secretaría, nunca el partido, y hace
  // falta saber sobre qué dimensión (admisibilidad / análisis) se escribe.
  const puedeMotivo = esInterno && !!etapa;

  if (!puedeEditar) return null;

  const abrir = (e: React.MouseEvent) => {
    e.stopPropagation();
    setTitulo(doc.titulo || '');
    setTipo(doc.tipo);
    setMotivo(doc.observacion || '');
    setOpen(true);
  };

  const cambioTitulo = titulo.trim() !== (doc.titulo || '').trim();
  const cambioTipo = puedeMover && tipo !== doc.tipo;
  const cambioMotivo = puedeMotivo && motivo.trim() !== (doc.observacion || '').trim();
  const hayCambios = cambioTitulo || cambioTipo || cambioMotivo;

  const guardar = async () => {
    if (!hayCambios || guardando) return;
    if (!titulo.trim()) {
      toast.error('El nombre no puede quedar vacío');
      return;
    }
    // Un rechazo sin motivo no se guarda: es el texto que se le transcribe al
    // partido. El backend valida lo mismo.
    if (cambioMotivo && !motivo.trim() && doc.estadoRevision === 'RECHAZADO') {
      toast.error('Un documento rechazado no puede quedar sin motivo');
      return;
    }
    setGuardando(true);
    try {
      // Dos endpoints distintos: los metadatos van por PATCH /documentos/:id y el
      // motivo por PATCH /documentos/:id/observacion, que no toca estados.
      if (cambioTitulo || cambioTipo) {
        await documentoService.updateMetadata(doc.id, {
          ...(cambioTitulo && { titulo: titulo.trim() }),
          ...(cambioTipo && { tipo }),
        });
      }
      if (cambioMotivo) {
        await documentoService.updateObservacion(doc.id, etapa!, motivo.trim() || null);
      }

      // 1) Parche inmediato de la caché: el documento cambia de nombre —y salta
      //    de sección si cambió el tipo— apenas se cierra el modal, sin esperar
      //    el viaje de ida y vuelta al servidor y sin recargar la página.
      //    `setQueriesData` con la key parcial alcanza a las consultas de
      //    /documentos cualquiera sea su filtro (partido, expediente, fechas).
      queryClient.setQueriesData<any>({ queryKey: ['documentos'] }, (prev: any) => {
        if (!Array.isArray(prev)) return prev;
        return prev.map((d: any) => (d.id === doc.id
          ? { ...d, ...(cambioTitulo && { titulo: titulo.trim() }), ...(cambioTipo && { tipo }) }
          : d));
      });

      // 2) Y después se revalida contra el servidor, que es quien tiene la
      //    última palabra (y de paso refresca lo que el parche no toca: los
      //    contadores del expediente y el resumen de documentación).
      queryClient.invalidateQueries({ queryKey: ['documentos'] });
      queryClient.invalidateQueries({ queryKey: ['documentos-checklist'] });
      queryClient.invalidateQueries({ queryKey: ['autoridades-dni'] });
      queryClient.invalidateQueries({ queryKey: ['apoderados-dni'] });
      queryClient.invalidateQueries({ queryKey: ['expediente'] });
      queryClient.invalidateQueries({ queryKey: ['resumen-documentacion'] });
      toast.success(
        cambioTipo ? 'Documento movido de sección'
        : cambioTitulo ? 'Documento renombrado'
        : 'Motivo actualizado',
      );
      setOpen(false);
      onSaved?.();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        'No se pudo guardar el cambio',
      );
    } finally {
      setGuardando(false);
    }
  };

  return (
    <>
      <button
        onClick={abrir}
        title={puedeMover ? 'Renombrar o mover de sección' : 'Renombrar'}
        className="text-xs text-gray-500 hover:text-blue-600 flex-shrink-0 inline-flex items-center gap-1"
      >
        <PencilSquareIcon className="h-4 w-4" />
        Editar
      </button>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title={puedeMover ? 'Editar documento' : 'Renombrar documento'}
        size="md"
      >
        <div className="space-y-4" onClick={(e) => e.stopPropagation()}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
            <input
              type="text"
              value={titulo}
              autoFocus
              maxLength={200}
              onChange={(e) => setTitulo(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') guardar(); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-400">{titulo.length}/200</p>
          </div>

          {puedeMover ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sección (tipo de documento)</label>
              <TipoDocumentoSelect
                value={tipo}
                onChange={setTipo}
                tiposPermitidos={tiposPermitidos}
                tiposExtra={[doc.tipo]}
              />
              {cambioTipo && (
                <p className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                  Pasa de <strong>{tipoDocLabels[doc.tipo] || doc.tipo}</strong> a{' '}
                  <strong>{tipoDocLabels[tipo] || tipo}</strong>. Conserva su estado de
                  verificación actual.
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-500">
              Sección: <strong>{tipoDocLabels[doc.tipo] || doc.tipo}</strong>.{' '}
              {esInterno
                ? 'Los documentos de DNI se administran desde la sección de DNIs.'
                : 'Solo la Secretaría Electoral puede cambiar un documento de sección.'}
            </p>
          )}

          {puedeMotivo && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motivo de la revisión
                <span className="ml-1.5 text-xs font-normal text-gray-400">
                  ({etapa === 'ANALISIS' ? 'análisis' : 'admisibilidad'})
                </span>
              </label>
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={6}
                maxLength={MAX_MOTIVO_DOCUMENTO}
                placeholder="Por qué se verificó, se rechazó o se volvió a pendiente…"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-400">
                {motivo.length.toLocaleString('es-AR')}/{MAX_MOTIVO_DOCUMENTO.toLocaleString('es-AR')} — corrige el texto del motivo sin cambiar el estado del documento.
                {doc.estadoRevision === 'RECHAZADO' && ' Al estar rechazado, no puede quedar vacío.'}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setOpen(false)}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={guardar}
              disabled={!hayCambios || guardando}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
